import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import logo from '../assets/logo.svg';
import bgImage from '../assets/bg.webp';
import { buildInviteContinueUrl } from '../hooks/useInviteContinuation';

interface InviteInfo {
  workspaceName: string;
  inviterName: string;
  inviterEmail: string;
  role: string;
  email: string;
  expiresAt: string;
}

/** Where the server says this recipient goes next. Server-decided, never
 *  inferred here and never chosen by the user. */
type NextStep = 'accept' | 'mismatch' | 'signin' | 'signup';

/**
 * Defense-in-depth check for an absolute URL the FE is about to navigate to.
 * Both the server-derived `destination` and the invite `returnTo` are
 * produced/validated server-side; this re-validates client-side before we
 * ever set window.location, so a misconfigured or tampered response can never
 * bounce the user to an off-suite origin. Allowed: https on a LevelUp-owned
 * suite domain (roilevelup.com — incl. staging subdomains — or the levelup.dev
 * dev domain used by non-prod app registry defaults).
 */
export function isTrustedSuiteUrl(candidate: string | null | undefined): boolean {
  if (!candidate) return false;
  try {
    const url = new URL(candidate);
    if (url.protocol !== 'https:') return false;
    const host = url.hostname;
    return (
      host === 'roilevelup.com' ||
      host.endsWith('.roilevelup.com') ||
      host === 'levelup.dev' ||
      host.endsWith('.levelup.dev')
    );
  } catch {
    return false;
  }
}

/** A Central-relative, same-origin path ("/…") — but NOT a protocol-relative
 * "//host" URL, which the browser would treat as an off-origin navigation. */
export function isSafeRelativePath(candidate: string | null | undefined): boolean {
  return !!candidate && candidate.startsWith('/') && !candidate.startsWith('//');
}

/**
 * Choose where to send the user after a successful invite acceptance.
 *
 * The canonical rule lives SERVER-SIDE (see inviteDestinationService): the
 * user goes to the app the invite originated from — originatingAppId outranks
 * returnTo, which outranks a single granted app, which outranks the Central
 * workspace landing. The backend returns the resolved `destination` (an
 * absolute app URL or a Central-relative path). The FE only navigates + guards:
 *
 *   1. `destination` from the backend:
 *        - a Central-relative path ("/…") → same-origin, navigate.
 *        - an absolute URL → must pass the trusted-suite-origin check
 *          (defense in depth vs a tampered/misconfigured response).
 *   2. Legacy fallback (older backend that has not yet populated
 *      `destination`): validated `returnTo`, then workspace settings, then
 *      `/settings/general`.
 */
export function pickPostAcceptDestination(
  destination: string | null | undefined,
  returnTo: string | null | undefined,
  workspaceId: string | null | undefined
): string {
  if (destination) {
    if (isSafeRelativePath(destination)) {
      return destination;
    }
    if (isTrustedSuiteUrl(destination)) {
      return destination;
    }
    // Absolute but untrusted — ignore and fall through to safe fallbacks.
  }

  // Legacy fallback path (pre-`destination` backend).
  if (isTrustedSuiteUrl(returnTo)) {
    return returnTo as string;
  }

  if (workspaceId) {
    return `/workspace/${workspaceId}/settings/general`;
  }

  return '/settings/general';
}

/**
 * AcceptInvite — the invitation entry point. NOT an authentication page.
 *
 * It used to carry its own email/password form, which meant LevelUp had two
 * competing sign-in experiences: this one, and the canonical pages every other
 * app sends people to. This route now only ROUTES, and the server makes the
 * decision:
 *
 *   valid invite + no session + account exists     → canonical /auth/login
 *   valid invite + no session + no account         → canonical /auth/signup
 *   valid invite + session IS the invited identity → accept, straight through
 *   valid invite + session is someone else         → account mismatch
 *   invalid / expired / revoked / already accepted → unchanged behaviour
 *
 * The invitation survives the trip through the canonical pages in the
 * `continue` parameter those pages already implement for OIDC, so no second
 * continuation mechanism exists. After authentication the user lands back here
 * with a session and the acceptance happens without another password.
 */
export function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [nextStep, setNextStep] = useState<NextStep | null>(null);
  const [invalidReason, setInvalidReason] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  // SEAT_LIMIT_EXCEEDED is not a broken invitation: the workspace (for a
  // personal workspace, the household) is full until a seat frees up.
  const [seatLimitReached, setSeatLimitReached] = useState(false);
  const [success, setSuccess] = useState(false);

  // Acceptance is a one-shot side effect. StrictMode double-invokes effects in
  // development, and a user can reload mid-flight; the server is idempotent
  // either way, but firing once keeps the UI honest.
  const acceptStarted = useRef(false);

  // Validate the invite and read the server's routing decision.
  useEffect(() => {
    if (!token) {
      setInvalidReason('No invite token provided');
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function validate() {
      try {
        const res = await fetch(`/api/auth/invites/validate?token=${encodeURIComponent(token)}`, {
          credentials: 'include',
        });
        const data = await res.json();
        if (cancelled) return;

        if (data.ok && data.valid) {
          setInvite(data.invite);
          setNextStep((data.nextStep as NextStep) ?? null);
        } else {
          setInvalidReason(data.reason || 'Invalid invite');
        }
      } catch {
        if (!cancelled) setInvalidReason('Failed to validate invite');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    validate();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Act on the decision.
  useEffect(() => {
    if (!nextStep || !token) return;

    // Send the recipient to the CANONICAL page, carrying the invitation in the
    // `continue` parameter those pages already understand.
    if (nextStep === 'signin' || nextStep === 'signup') {
      const target = nextStep === 'signin' ? '/auth/login' : '/auth/signup';
      navigate(`${target}?continue=${encodeURIComponent(buildInviteContinueUrl(token))}`, {
        replace: true,
      });
      return;
    }

    if (nextStep !== 'accept' || acceptStarted.current) return;
    acceptStarted.current = true;

    (async () => {
      try {
        const res = await fetch('/api/auth/invites/accept-session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          // The token and nothing else: workspace, role and invited address
          // are the invitation's, read server-side.
          body: JSON.stringify({ inviteToken: token }),
        });
        const data = await res.json();

        if (!res.ok) {
          setSeatLimitReached(data.code === 'SEAT_LIMIT_EXCEEDED');
          setError(data.error || 'Failed to accept invite');
          return;
        }

        setSuccess(true);
        const redirectUrl = pickPostAcceptDestination(
          data.destination,
          data.returnTo,
          data.workspaceId
        );
        setTimeout(() => {
          window.location.href = redirectUrl;
        }, 1200);
      } catch {
        setError('Network error. Please try again.');
      }
    })();
  }, [nextStep, token, navigate]);

  const signInAsInvited = () => {
    navigate(`/auth/login?continue=${encodeURIComponent(buildInviteContinueUrl(token))}`, {
      replace: true,
    });
  };

  // Sign out through the EXISTING session endpoint, then send the user to the
  // canonical Sign In page still carrying the invitation. No second
  // account-switching mechanism is introduced here.
  const switchAccount = async () => {
    try {
      await fetch('/api/oidc/session/logout', { method: 'POST', credentials: 'include' });
    } catch {
      // Sign-out is best effort; the canonical page can still be reached.
    }
    signInAsInvited();
  };

  /**
   * THE SAME SHELL AS THE CANONICAL AUTH PAGES.
   *
   * These screens sit in the middle of the invitation journey — between the
   * email and /auth/login — so anything else reads as a different product
   * halfway through signing in. The logo belongs ABOVE the card, at the size
   * the auth pages use: inside the card it was squeezed into a 32px box that
   * clipped the wordmark into a stray arrow and the letters "Up".
   */
  const shell = (children: React.ReactNode) => (
    <div
      className="min-h-screen bg-[rgb(24,24,24)] bg-cover bg-center flex flex-col items-center p-4"
      style={{ backgroundImage: `url(${bgImage})` }}
    >
      <img src={logo} alt="LevelUp" className="h-10 mt-8" />
      <div className="flex-1 flex items-center justify-center w-full">
        <div className="bg-white shadow-lg rounded-lg p-8 w-full max-w-md text-center">{children}</div>
      </div>
    </div>
  );

  const primaryButton =
    'w-full bg-green-700 text-white py-2.5 px-4 rounded-md font-medium hover:bg-green-800 transition-colors';
  const quietLink = 'text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors';

  // Loading, or a decision that resolves to a redirect — never show a form.
  if (isLoading || nextStep === 'signin' || nextStep === 'signup') {
    return shell(
      <div className="py-4">
        <div className="animate-spin w-8 h-8 border-2 border-green-700 border-t-transparent rounded-full mx-auto" />
        <p className="mt-4 text-sm text-gray-500">Checking your invitation...</p>
      </div>
    );
  }

  // Invalid / expired / revoked / already accepted — unchanged behaviour.
  if (invalidReason) {
    return shell(
      <>
        <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-7 h-7 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Invitation unavailable</h1>
        <p className="text-sm text-gray-500 mb-6">{invalidReason}</p>
        <Link to="/auth/login" className={primaryButton + ' inline-block'}>
          Go to sign in
        </Link>
      </>
    );
  }

  // Signed in as somebody else. The invitation is NOT accepted, no membership
  // is created, and nothing is rewritten to point at the current account.
  if (nextStep === 'mismatch') {
    return shell(
      <>
        <div className="w-14 h-14 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-7 h-7 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
            />
          </svg>
        </div>
        <h1 className="text-xl font-semibold text-gray-900 mb-2">You're signed in as someone else</h1>
        <p className="text-sm leading-relaxed text-gray-500 mb-1">This invitation was sent to</p>
        <p className="mb-6 break-all text-sm font-semibold text-gray-900">{invite?.email}</p>
        <button type="button" onClick={switchAccount} className={primaryButton}>
          Sign out and switch account
        </button>
        <p className="mt-4 text-xs leading-relaxed text-gray-400">
          Your invitation stays valid — nothing has been accepted on this account.
        </p>
      </>
    );
  }

  if (success) {
    return shell(
      <>
        <div className="w-14 h-14 bg-green-700/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-7 h-7 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-xl font-semibold text-gray-900 mb-2">You're in</h1>
        <p className="text-sm text-gray-500">
          Welcome to <strong className="text-gray-900">{invite?.workspaceName}</strong>. Taking you
          there now...
        </p>
      </>
    );
  }

  if (error) {
    return shell(
      <>
        <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-7 h-7 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z" />
          </svg>
        </div>
        <h1 className="text-xl font-semibold text-gray-900 mb-2">
          {seatLimitReached
            ? `${invite?.workspaceName ?? 'This workspace'} has no free seats`
            : "We couldn't complete your invitation"}
        </h1>
        <p className="text-sm text-gray-500 mb-6">{error}</p>
        <button type="button" onClick={signInAsInvited} className={quietLink}>
          Go to sign in
        </button>
      </>
    );
  }

  // Accepting for a correctly authenticated user.
  return shell(
    <div className="py-2">
      <div className="animate-spin w-8 h-8 border-2 border-green-700 border-t-transparent rounded-full mx-auto mb-4" />
      <p className="text-sm text-gray-600">
        Joining <strong className="text-gray-900">{invite?.workspaceName}</strong>...
      </p>
    </div>
  );
}
